import { supabase } from './supabaseClient';

export interface UploadProgress {
  progress: number;
  total: number;
  loaded: number;
}

export interface UploadResult {
  url: string;
  path: string;
  size: number;
  type: string;
  name: string;
}

export class FileUploadService {
  private readonly maxFileSize = 10 * 1024 * 1024; // 10MB
  private readonly allowedTypes = [
    'image/jpeg',
    'image/png', 
    'image/gif',
    'image/webp',
    'application/pdf',
    'text/plain',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ];

  validateFile(file: File): { isValid: boolean; error?: string } {
    if (file.size > this.maxFileSize) {
      return {
        isValid: false,
        error: `File size must be less than ${this.maxFileSize / 1024 / 1024}MB`
      };
    }

    if (!this.allowedTypes.includes(file.type)) {
      return {
        isValid: false,
        error: 'File type not supported'
      };
    }

    return { isValid: true };
  }

  async uploadForumAttachment(
    file: File,
    topicId?: string,
    onProgress?: (progress: UploadProgress) => void
  ): Promise<UploadResult> {
    const validation = this.validateFile(file);
    if (!validation.isValid) {
      throw new Error(validation.error);
    }

    // Get authenticated user
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) {
      throw new Error('User must be authenticated to upload files');
    }

    // Generate unique file path - simpler structure for public bucket
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
    
    // Use simpler path structure for public bucket
    const folder = topicId ? `topics/${topicId}` : `temp`;
    const filePath = `${folder}/${fileName}`;

    try {
      const { data, error } = await supabase.storage
        .from('forum-attachments')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        console.error('Supabase upload error:', error);
        throw error;
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('forum-attachments')
        .getPublicUrl(filePath);

      return {
        url: urlData.publicUrl,
        path: filePath,
        size: file.size,
        type: file.type,
        name: file.name
      };
    } catch (error) {
      console.error('Upload error:', error);
      throw new Error(`Failed to upload file: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async uploadMultipleFiles(
    files: File[],
    topicId: string,
    onProgress?: (fileIndex: number, progress: UploadProgress) => void
  ): Promise<UploadResult[]> {
    const results: UploadResult[] = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        const result = await this.uploadForumAttachment(
          file,
          topicId,
          onProgress ? (progress) => onProgress(i, progress) : undefined
        );
        results.push(result);
      } catch (error) {
        console.error(`Failed to upload file ${file.name}:`, error);
        // Continue with other files
      }
    }

    return results;
  }

  async deleteFile(filePath: string): Promise<void> {
    const { error } = await supabase.storage
      .from('forum-attachments')
      .remove([filePath]);

    if (error) throw error;
  }

  async uploadAvatar(file: File, userId: string): Promise<UploadResult> {
    const validation = this.validateFile(file);
    if (!validation.isValid) {
      throw new Error(validation.error);
    }

    // Only allow images for avatars
    if (!file.type.startsWith('image/')) {
      throw new Error('Avatar must be an image file');
    }

    const fileExt = file.name.split('.').pop();
    const fileName = `avatar-${userId}.${fileExt}`;
    const filePath = `avatars/${fileName}`;

    try {
      const { data, error } = await supabase.storage
        .from('forum-attachments')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true // Allow overwriting existing avatar
        });

      if (error) throw error;

      const { data: urlData } = supabase.storage
        .from('forum-attachments')
        .getPublicUrl(filePath);

      return {
        url: urlData.publicUrl,
        path: filePath,
        size: file.size,
        type: file.type,
        name: file.name
      };
    } catch (error) {
      console.error('Avatar upload error:', error);
      throw new Error('Failed to upload avatar');
    }
  }

  async getFileInfo(filePath: string): Promise<any> {
    const { data, error } = await supabase.storage
      .from('forum-attachments')
      .list(filePath.split('/').slice(0, -1).join('/'));

    if (error) throw error;

    const fileName = filePath.split('/').pop();
    return data.find(file => file.name === fileName);
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  getFileTypeIcon(mimeType: string): string {
    if (mimeType.startsWith('image/')) return 'fa-image';
    if (mimeType === 'application/pdf') return 'fa-file-pdf';
    if (mimeType.includes('document') || mimeType.includes('word')) return 'fa-file-word';
    if (mimeType === 'text/plain') return 'fa-file-text';
    return 'fa-file';
  }

  async createStorageBucket(): Promise<void> {
    try {
      const { error } = await supabase.storage.createBucket('forum-attachments', {
        public: true,
        allowedMimeTypes: this.allowedTypes,
        fileSizeLimit: this.maxFileSize
      });

      if (error && error.message !== 'Bucket already exists') {
        throw error;
      }
    } catch (error) {
      console.error('Error creating storage bucket:', error);
    }
  }

  async uploadProjectImage(
    file: File,
    projectId: string,
    featureIndex?: number,
    onProgress?: (progress: UploadProgress) => void
  ): Promise<UploadResult> {
    const validation = this.validateFile(file);
    if (!validation.isValid) {
      throw new Error(validation.error);
    }

    // Get authenticated user
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) {
      throw new Error('User must be authenticated to upload files');
    }

    // Generate unique file path
    const fileExt = file.name.split('.').pop();
    const timestamp = Date.now();
    const featurePart = featureIndex !== undefined ? `_feature_${featureIndex}` : '';
    const fileName = `${user.id}/${projectId}/image${featurePart}_${timestamp}.${fileExt}`;

    // Try multiple storage approaches
    const bucketOptions = ['project-screenshots', 'forum-attachments'];
    let lastError = null;

    for (const bucketName of bucketOptions) {
      try {
        // Ensure bucket exists
        await this.ensureBucketExists(bucketName);

        // Upload file
        const { data, error } = await supabase.storage
          .from(bucketName)
          .upload(fileName, file, {
            cacheControl: '3600',
            upsert: false,
            contentType: file.type
          });

        if (error) {
          lastError = error;
          
          // If it's an RLS issue, try with public path
          if (error.message?.includes('row-level security') || 
              error.message?.includes('Unauthorized')) {
            
            const publicFileName = `public/${fileName}`;
            const { data: publicData, error: publicError } = await supabase.storage
              .from(bucketName)
              .upload(publicFileName, file, {
                cacheControl: '3600',
                upsert: false,
                contentType: file.type
              });

            if (!publicError) {
              // Success with public path
              const { data: urlData } = supabase.storage
                .from(bucketName)
                .getPublicUrl(publicFileName);

              return {
                url: urlData.publicUrl,
                path: publicFileName,
                size: file.size,
                type: file.type,
                name: file.name
              };
            }
          }
          
          // Try next bucket
          continue;
        }

        // Success - get public URL
        const { data: urlData } = supabase.storage
          .from(bucketName)
          .getPublicUrl(fileName);

        return {
          url: urlData.publicUrl,
          path: fileName,
          size: file.size,
          type: file.type,
          name: file.name
        };

      } catch (bucketError) {
        lastError = bucketError;
        continue;
      }
    }

    // All buckets failed
    const errorMessage = lastError instanceof Error ? lastError.message : 'Unknown upload error';
    throw new Error(`Failed to upload file to any available storage bucket: ${errorMessage}`);
  }

  private async ensureBucketExists(bucketName: string): Promise<void> {
    try {
      const { data: buckets } = await supabase.storage.listBuckets();
      const bucketExists = buckets?.some(bucket => bucket.name === bucketName);
      
      if (!bucketExists) {
        await supabase.storage.createBucket(bucketName, {
          public: true,
          allowedMimeTypes: this.allowedTypes,
          fileSizeLimit: this.maxFileSize
        });
      }
    } catch (error) {
      // Non-critical error - bucket might already exist or we might not have permissions
      console.warn(`Could not ensure bucket ${bucketName} exists:`, error);
    }
  }

  async checkStoragePermissions(): Promise<{ canUpload: boolean; buckets: string[]; errors: string[] }> {
    const result = {
      canUpload: false,
      buckets: [] as string[],
      errors: [] as string[]
    };

    try {
      // Check if user is authenticated
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        result.errors.push('User not authenticated');
        return result;
      }

      // Check available buckets
      const { data: buckets, error: listError } = await supabase.storage.listBuckets();
      if (listError) {
        result.errors.push(`Failed to list buckets: ${listError.message}`);
        return result;
      }

      const targetBuckets = ['project-screenshots', 'forum-attachments'];
      const availableBuckets = buckets?.map(b => b.name) || [];
      
      for (const bucketName of targetBuckets) {
        if (availableBuckets.includes(bucketName)) {
          result.buckets.push(bucketName);

          // Try a test upload to check permissions
          try {
            const testFile = new Blob(['test'], { type: 'text/plain' });
            const testFileName = `test/${user.id}/permission_test_${Date.now()}.txt`;
            
            const { error: uploadError } = await supabase.storage
              .from(bucketName)
              .upload(testFileName, testFile, { upsert: false });

            if (!uploadError) {
              result.canUpload = true;
              // Clean up test file
              await supabase.storage.from(bucketName).remove([testFileName]);
            } else {
              result.errors.push(`Upload test failed for ${bucketName}: ${uploadError.message}`);
            }
          } catch (testError) {
            result.errors.push(`Upload test error for ${bucketName}: ${testError instanceof Error ? testError.message : 'Unknown error'}`);
          }
        }
      }

      return result;
    } catch (error) {
      result.errors.push(`General error: ${error instanceof Error ? error.message : 'Unknown error'}`);
      return result;
    }
  }
}

export const fileUploadService = new FileUploadService();
